import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogPostsMetadata } from '../data/blogData';
import BlogPost from '../components/BlogPost';
import { BlogPostMetadata } from '../types';
import { trackEvent, EventType } from '../utils/analytics';
import SEO from '../components/SEO';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostMetadata | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!slug) {
          throw new Error('No slug provided');
        }
        
        console.log(`Looking for blog post with slug: ${slug}`);
        console.log(`Available posts: ${blogPostsMetadata.length}`);
        
        // Find the post by slug
        const foundPost = blogPostsMetadata.find(post => post.slug === slug);
        
        if (!foundPost) {
          console.error(`Blog post with slug "${slug}" not found`);
          throw new Error(`Blog post not found: ${slug}`);
        }
        
        console.log(`Found blog post: ${foundPost.title}`);
        setPost(foundPost);
        
        // Track the view
        trackEvent(EventType.BLOG_POST_VIEW, {
          page: `/blog/${slug}`,
          post_title: foundPost.title,
          post_slug: slug
        });
      } catch (error) {
        console.error('Error loading blog post:', error);
        setError(error instanceof Error ? error.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    loadPost();
  }, [slug]);

  // Handle back to blog button click
  const handleBackToBlog = () => {
    navigate('/blog');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden p-8">
          <h1 className="text-3xl font-bold text-red-600 dark:text-red-400 mb-4">Error Loading Blog Post</h1>
          <p className="text-gray-700 dark:text-gray-300 mb-6">{error || 'Blog post not found'}</p>
          <button
            onClick={handleBackToBlog}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      {post && (
        <SEO
          title={`${post.title} | David Sheeshka's Blog`}
          description={post.excerpt}
          keywords={post.tags.join(', ') + ', web development, technology, software development'}
          image={post.image}
          url={`https://davidsheeshka.com/blog/${post.slug}`}
          type="article"
          article={true}
          publishedTime={post.date}
          author={post.author}
        />
      )}
      <div className="mb-8">
        <button
          onClick={handleBackToBlog}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 transition-colors"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Blog
        </button>
      </div>
      
      <BlogPost post={post} allPosts={blogPostsMetadata} />
    </div>
  );
};

export default BlogPostPage;
